// dropForeignKey — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'drop-foreign-key', constraintName })` op (DSE2 §3.B2).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice B2.
//
// Per-engine rules (mirrors DSE2's `synthesizeDropForeignKey`):
//   - sqlite: rejected on ALTER (recreate path opt-in lives in Slice H3).
//   - mssql / pgsql / oracle: emit `DROP CONSTRAINT name`.
//   - mysql: emits `DROP FOREIGN KEY name` (drop-by-name only — there is
//     no DROP-by-shape variant on any of the four supported engines).
//   - pgsql / oracle: optional `cascade?: boolean` appends `CASCADE`.

import type { DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import type { SqlEngine } from '../../../../sql-grammar/model';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export type DropForeignKeyArgs = {
  readonly constraintName: string;
  readonly cascade?: boolean;
};

export function buildDropForeignKeyEdit(args: DropForeignKeyArgs): DdlEdit {
  return {
    op: 'drop-foreign-key',
    constraintName: args.constraintName,
    cascade: args.cascade,
  };
}

export async function commitDropForeignKey(
  ctx: VqeDdlEditContext,
  args: DropForeignKeyArgs,
): Promise<void> {
  const edit = buildDropForeignKeyEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

export function isDropForeignKeySupported(engine: SqlEngine): boolean {
  return engine !== 'sqlite';
}

export function getDropForeignKeyUnsupportedTooltip(engine: SqlEngine): string {
  if (engine === 'sqlite') {
    return (
      'SQLite does not support ALTER TABLE … DROP FOREIGN KEY. Recreate '
      + 'the table without the constraint — use “Edit as SQL” to write '
      + 'the change.'
    );
  }
  return '';
}

/** Every supported engine requires the constraint name on FK drop —
 *  DSE2's contract is drop-by-name only. mysql additionally cannot drop
 *  anonymous FKs (FKs added without an explicit name) because the auto-
 *  generated name is engine-specific (`<table>_ibfk_<n>`). */
export function isCascadeSupported(engine: SqlEngine): boolean {
  return engine === 'postgres' || engine === 'oracle';
}

/** Pre-flight validator: the synthesizer always requires `constraintName`
 *  (mssql / pgsql / mysql / oracle); cascade rejected on mssql + mysql. */
export function validateDropForeignKeyForEngine(
  engine: SqlEngine,
  args: DropForeignKeyArgs,
): string | null {
  if (engine === 'sqlite') return getDropForeignKeyUnsupportedTooltip(engine);
  const name = args.constraintName.trim();
  if (name === '') {
    return `${engine} requires the constraint name to drop a foreign key.`;
  }
  if (!isCascadeSupported(engine) && args.cascade === true) {
    return 'CASCADE is supported on PostgreSQL + Oracle only.';
  }
  return null;
}
